import dayjs, { Dayjs } from 'dayjs'
import { useState, useEffect, useMemo } from 'react'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  AdvertiserPerformanceStatsFullPartsFragment,
  AdvertiserPerformanceStatsPartsFragment,
  PublisherPerformanceStatsPartsFragment,
} from '../../../__generated__/graphql'
import { PerformanceStatsTimeFilters } from '../../../components/stats/filters/useStatsFilterStorage'
import { CONF_CHART_MAX_LINES_FOR_VALUE_DISPLAY } from '../../../env'
import { StatsEntity } from '../../../interfaces/stats'
import { useLocalStorage } from '../useLocalStorage'
import { StatsConfKeys } from '../../../components/stats/statsConf'
import { useIsPwsUser } from '../useUserContext'
import { getChartDateFormat } from './chartDateFormat'

dayjs.extend(utc)
dayjs.extend(timezone)

export { CHART_DATE_FORMAT } from './chartDateFormat'

export const ANALYTICS_CHART_ITEMS: StatsConfKeys[] = ['pageViews', 'sessions']

export type ChartOptionsType = {
  displayValues: boolean
}

export type PerformanceStatsMetrics =
  | AdvertiserPerformanceStatsPartsFragment
  | AdvertiserPerformanceStatsFullPartsFragment
  | PublisherPerformanceStatsPartsFragment

export type TimeseriesType = {
  timeseries: {
    date: Dayjs
    stats: PerformanceStatsMetrics
  }[]
  stats: PerformanceStatsMetrics
}

export type TimeseriesDataType<T> = T & TimeseriesType

type UsePerformanceStatsReturnType<T> = {
  timeseriesData: TimeseriesDataType<T> | undefined
  chartItemsVisibility: Record<StatsConfKeys, boolean>
  toggleVisibility: (name: StatsConfKeys) => void
  chartOptions: ChartOptionsType
  setChartOptions: React.Dispatch<React.SetStateAction<ChartOptionsType>>
  chartItems: StatsConfKeys[]
  dateFormat: string
  timezone: string
  setDateFormat: React.Dispatch<React.SetStateAction<string>>
}
export const useTimeseries = <T extends TimeseriesType>(
  timeseries: TimeseriesDataType<T> | undefined,
  options: {
    filters: PerformanceStatsTimeFilters
    entity: StatsEntity
  }
): UsePerformanceStatsReturnType<T> => {
  const isPwsUser = useIsPwsUser()
  const { filters, entity } = options
  const [dateFormat, setDateFormat] = useState<string>(
    getChartDateFormat(filters.granularity, filters.startDate, filters.endDate, filters.timeZone)
  )

  useEffect(() => {
    setDateFormat(getChartDateFormat(filters.granularity, filters.startDate, filters.endDate, filters.timeZone))
  }, [filters.endDate, filters.granularity, filters.startDate, filters.timeZone])
  const [chartOptions, setChartOptions] = useState<ChartOptionsType>({ displayValues: false })
  const [chartItemsVisibility, setChartItemsVisibility] = useLocalStorage<Record<StatsConfKeys, boolean>>(
    'performanceStats.widgets',
    {} as Record<StatsConfKeys, boolean>
  )

  const isPublisherStats = useMemo(
    () =>
      [StatsEntity.Websites, StatsEntity.Positions, StatsEntity.ResellerPublisher, StatsEntity.Publishers].includes(
        entity
      ),
    [entity]
  )

  const hasAnalyticsStats = useMemo(() => {
    if (entity !== StatsEntity.AdGroups) return false

    const analyticsTotals = timeseries?.stats as Partial<AdvertiserPerformanceStatsFullPartsFragment> | undefined
    return ANALYTICS_CHART_ITEMS.some((key) => (analyticsTotals?.[key] ?? 0) > 0)
  }, [entity, timeseries?.stats])

  const chartItems: StatsConfKeys[] = useMemo(() => {
    if (isPublisherStats) {
      if (isPwsUser) return ['displays', 'impressions', 'clicks', 'leads', 'ctr', 'cpc', 'rpm', 'cpm', 'commissions']

      return ['displays', 'impressions', 'clicks', 'ctr', 'cpc', 'rpm', 'cpm', 'commissions']
    }

    const advertiserItems: StatsConfKeys[] = [
      'impressions',
      'clicks',
      'ctr',
      'cpc',
      'leads',
      'cpl',
      'orders',
      'cpo',
      'cpm',
      'costs',
    ]
    if (hasAnalyticsStats) return [...advertiserItems, ...ANALYTICS_CHART_ITEMS]

    return advertiserItems
  }, [isPublisherStats, isPwsUser, hasAnalyticsStats])

  const updateValueVisibility = (chartItemsVisibility: Record<StatsConfKeys, boolean>): void => {
    const visibleItemCount = chartItems.filter((item) => chartItemsVisibility[item]).length
    setChartOptions(
      Object.assign({}, chartOptions, { displayValues: visibleItemCount <= CONF_CHART_MAX_LINES_FOR_VALUE_DISPLAY })
    )
  }

  useEffect(() => {
    const newValues = chartItems.reduce(
      (acc, name) => Object.assign({}, acc, { [name]: !ANALYTICS_CHART_ITEMS.includes(name) }),
      {}
    )
    setChartItemsVisibility(Object.assign({}, newValues, chartItemsVisibility))
    updateValueVisibility(Object.assign({}, newValues, chartItemsVisibility))
  }, [chartItems])

  const toggleVisibility = (name: StatsConfKeys): void => {
    const newValues = Object.assign({}, chartItemsVisibility, { [name]: !chartItemsVisibility[name] })
    updateValueVisibility(newValues)
    setChartItemsVisibility(newValues)
  }

  const updatedTimeseries = useMemo(() => {
    if (!timeseries) return undefined

    const NewTimeseries =
      timeseries.timeseries === undefined
        ? []
        : timeseries.timeseries
            .map((singleTimeSeries) => ({
              date: dayjs(singleTimeSeries.date).tz(filters.timeZone),
              stats: singleTimeSeries.stats,
            }))
            .sort((a, b) => (a.date.isBefore(b.date) ? -1 : 1))

    return { ...timeseries, timeseries: NewTimeseries }
  }, [timeseries])

  return {
    timeseriesData: updatedTimeseries,
    chartItemsVisibility,
    toggleVisibility,
    chartOptions,
    setChartOptions,
    chartItems,
    dateFormat,
    timezone: 'Europe/Paris',
    setDateFormat,
  }
}
